import { ChangeDetectionStrategy, Component, DestroyRef, effect, inject, signal, untracked } from '@angular/core';
import { HdContentLanguageService } from '../../chart/bodygraph/hd-content-language.service';
import { IChingMethodContent } from '../core/iching.models';
import { HdIChingContentService } from '../hd-iching-content.service';
import { injectIChingTranslate } from './iching-consult-translate';

export const HD_ICHING_GUIDE_PANEL_KEY = 'classic-iching-guide';

/**
 * De gids in de app: introductie, de acht stappen (titel, kernzin en
 * uitleg), de legende van de worp en de begrippenlijst. Alleen lezen. Het
 * paneel laadt de methode zelf in de content-taal en volgt dus een taalwissel
 * (zelfde recept als het leespaneel: laadgeneratie, vorige content blijft staan
 * tot de nieuwe er is).
 */
@Component({
  selector: 'hd-iching-guide-panel',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: `
    /*
     * Zwevend (dialoog): een maat kleiner dan de volle body-cap en met een
     * breedtecap op :host, zodat de dialoog binnen beeld blijft en de
     * sluitknop zichtbaar is (fix 2026-10-18; de phone-sheet-override van de
     * floating-panel-host wint automatisch). Gedockt: vult het dock-pane.
     */
    :host { display: block; width: min(560px, 70vw); max-height: min(70vh, 720px); overflow-y: auto; }
    :host-context(.docked-panels-content-pane) { width: auto; max-height: none; height: 100%; }
    .guide { padding: 14px 18px 24px; font-size: 13.5px; line-height: 1.55; }
    h3 { margin: 0 0 10px; font-size: 17px; }
    h4 { margin: 18px 0 6px; font-size: 15px; }
    h5 { margin: 12px 0 4px; font-size: 14px; }
    p { margin: 0 0 8px; }
    .lead { font-weight: 600; }
    .legend { padding: 8px 12px; border-radius: var(--radius-md, 6px); background: var(--hd-iching-card-bg, rgba(0, 0, 0, 0.035)); }
    dl { margin: 0; }
    dt { font-weight: 600; margin-top: 8px; }
    dd { margin: 2px 0 0; }
    .attribution { margin-top: 18px; font-size: 12px; opacity: 0.7; }
  `,
  template: `
    @if (method(); as m) {
      <article class="guide">
        <!-- Eerst de bedoeling van de pagina, dan pas wat de I Tjing is. -->
        <h3>{{ t('humanDesign.iching.consult.guidePurpose') }}</h3>
        @for (paragraph of m.steps.intro.purpose; track $index) {
          <p class="lead" data-test-id="iching-guide-purpose">{{ paragraph }}</p>
        }

        <h4>{{ m.steps.intro.title }}</h4>
        @for (paragraph of m.steps.intro.paragraphs; track $index) {
          <p>{{ paragraph }}</p>
        }

        @for (step of m.steps.steps; track step.id; let i = $index) {
          <section [attr.data-step]="step.id">
            <h4>{{ i + 1 }}. {{ step.title }}</h4>
            <p class="lead">{{ step.lead }}</p>
            @for (paragraph of step.more; track $index) {
              <p>{{ paragraph }}</p>
            }
            @if (step.legend; as legend) {
              <div class="legend">
                <h5>{{ t('humanDesign.iching.consult.guideLegend') }}</h5>
                <p>{{ legend.head }} · {{ legend.tail }}</p>
                <p>6: {{ legend.values['6'] }}</p>
                <p>7: {{ legend.values['7'] }}</p>
                <p>8: {{ legend.values['8'] }}</p>
                <p>9: {{ legend.values['9'] }}</p>
                <p>{{ legend.order }}</p>
                <p>{{ legend.interrupt }}</p>
              </div>
            }
          </section>
        }

        @if (m.terms.length > 0) {
          <h4>{{ t('humanDesign.iching.consult.guideTerms') }}</h4>
          <dl>
            @for (term of m.terms; track term.id) {
              <dt>{{ term.term }}</dt>
              <dd>{{ term.long }}</dd>
            }
          </dl>
        }

        <p class="attribution">{{ m.steps.attribution }}</p>
      </article>
    }
  `,
})
export class HdIChingGuidePanelComponent {
  private readonly content = inject(HdIChingContentService);
  private readonly contentLang = inject(HdContentLanguageService);

  protected readonly t = injectIChingTranslate();
  protected readonly method = signal<IChingMethodContent | undefined>(undefined);

  /** Alleen de laatst gestarte load mag de content zetten; na destroy niemand meer. */
  private generation = 0;

  constructor() {
    inject(DestroyRef).onDestroy(() => this.generation++);
    effect(() => {
      const lang = this.contentLang.effectiveLang();
      untracked(() => void this.load(lang));
    });
  }

  private async load(lang: string): Promise<void> {
    const generation = ++this.generation;
    try {
      const method = await this.content.loadMethod(lang);
      if (generation === this.generation) {
        this.method.set(method);
      }
    } catch {
      // Mislukt: de vorige content blijft staan (de pagina meldt laadfouten zelf).
    }
  }
}
